import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(
  supabaseUrl,
  supabaseAnonKey
  ,
  {
    global: {
      fetch: async (input, init) => {
        const baseFetch = (globalThis.fetch ? globalThis.fetch.bind(globalThis) : fetch) as typeof fetch

        if (!(import.meta as any)?.env?.DEV) {
          return baseFetch(input as any, init as any)
        }

        const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now()

        const req = (() => {
          try {
            if (typeof input === "string") return { url: input, method: String(init?.method || "GET").toUpperCase(), body: init?.body }
            if (input instanceof URL) return { url: input.toString(), method: String(init?.method || "GET").toUpperCase(), body: init?.body }
            if (input instanceof Request) return { url: input.url, method: String(init?.method || input.method || "GET").toUpperCase(), body: init?.body }
          } catch {
            void 0
          }
          return { url: String((input as any)?.url || input || ""), method: String((init as any)?.method || "GET").toUpperCase(), body: (init as any)?.body }
        })()

        const payload = (() => {
          const b: any = req.body
          if (b == null) return null
          if (typeof b === "string") {
            const s = b.trim()
            if (!s) return null
            try {
              return JSON.parse(s)
            } catch {
              return s
            }
          }
          try {
            if (typeof FormData !== "undefined" && b instanceof FormData) return { _type: "FormData" }
            if (typeof Blob !== "undefined" && b instanceof Blob) return { _type: "Blob", size: b.size, mime: b.type || null }
            if (typeof ArrayBuffer !== "undefined" && b instanceof ArrayBuffer) return { _type: "ArrayBuffer", byteLength: b.byteLength }
            if (typeof Uint8Array !== "undefined" && b instanceof Uint8Array) return { _type: "Uint8Array", byteLength: b.byteLength }
          } catch {
            void 0
          }
          return { _type: typeof b }
        })()

        const classify = (urlRaw: string) => {
          let url: URL | null = null
          try {
            url = new URL(urlRaw)
          } catch {
            try {
              url = new URL(urlRaw, window.location.origin)
            } catch {
              url = null
            }
          }

          const pathname = url?.pathname || ""
          const endpoint =
            pathname.includes("/rest/v1/") ? "rest" :
              pathname.includes("/auth/v1/") ? "auth" :
                pathname.includes("/storage/v1/") ? "storage" :
                  pathname.includes("/functions/v1/") ? "functions" :
                    "unknown"

          const restIdx = pathname.indexOf("/rest/v1/")
          const restPath = restIdx >= 0 ? pathname.slice(restIdx + "/rest/v1/".length) : ""
          const rpcPrefix = "rpc/"
          const tableOrRpc = restPath ? restPath.split("/")[0] : ""
          const isRpc = tableOrRpc === "rpc" || restPath.startsWith(rpcPrefix)
          const rpc = isRpc ? restPath.replace(/^rpc\//, "").split("/")[0] : null
          const table = !isRpc && tableOrRpc ? decodeURIComponent(tableOrRpc) : null

          return { endpoint, table, rpc }
        }

        const meta = classify(req.url)
        const id = (() => {
          try {
            const w = window as any
            w.__SUPABASE_TRACE_SEQ__ = Number(w.__SUPABASE_TRACE_SEQ__ || 0) + 1
            return String(w.__SUPABASE_TRACE_SEQ__)
          } catch {
            return String(Date.now())
          }
        })()

        let res: Response
        let responseBody: any = null
        let responseText: string | null = null
        let parseError: string | null = null

        try {
          res = await baseFetch(input as any, init as any)
        } catch (e: any) {
          const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now()
          const entry = {
            id,
            ok: false,
            networkError: true,
            method: req.method,
            url: req.url,
            endpoint: meta.endpoint,
            table: meta.table,
            rpc: meta.rpc,
            payload,
            status: 0,
            statusText: "NETWORK_ERROR",
            response: null,
            error: e instanceof Error ? e.message : String(e),
            elapsedMs: endedAt - startedAt,
            ts: Date.now(),
          }
          try {
            const w = window as any
            w.__SUPABASE_TRACE__ = Array.isArray(w.__SUPABASE_TRACE__) ? w.__SUPABASE_TRACE__ : []
            w.__SUPABASE_TRACE__.push(entry)
            if (!w.__SUPABASE_FIRST_ERROR__) w.__SUPABASE_FIRST_ERROR__ = entry
          } catch {
            void 0
          }
          console.error("[SUPABASE_TRACE][ERROR][NETWORK]", entry)
          throw e
        }

        const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now()
        const contentType = res.headers?.get?.("content-type") || ""
        try {
          const cloned = res.clone()
          if (contentType.toLowerCase().includes("application/json")) {
            responseBody = await cloned.json().catch(() => null)
          } else {
            responseText = await cloned.text().catch(() => null)
            responseBody = responseText
          }
        } catch (e: any) {
          parseError = e instanceof Error ? e.message : String(e)
        }

        const entry = {
          id,
          ok: res.ok,
          method: req.method,
          url: req.url,
          endpoint: meta.endpoint,
          table: meta.table,
          rpc: meta.rpc,
          payload,
          status: res.status,
          statusText: res.statusText,
          response: responseBody,
          parseError,
          elapsedMs: endedAt - startedAt,
          ts: Date.now(),
        }

        try {
          const w = window as any
          w.__SUPABASE_TRACE__ = Array.isArray(w.__SUPABASE_TRACE__) ? w.__SUPABASE_TRACE__ : []
          w.__SUPABASE_TRACE__.push(entry)
          if (!res.ok && !w.__SUPABASE_FIRST_ERROR__) w.__SUPABASE_FIRST_ERROR__ = entry
        } catch {
          void 0
        }

        if (!res.ok) {
          console.error("[SUPABASE_TRACE][ERROR]", entry)
        } else {
          console.log("[SUPABASE_TRACE]", entry)
        }

        return res
      }
    }
  }
);

export type SupabaseAuthContext = {
  accessToken: string
  sessionUserId: string
  jwtSub: string
  authUid: string
}

function decodeBase64UrlJson<T>(value: string): T | null {
  try {
    const raw = String(value || "")
    const normalized = raw
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(raw.length / 4) * 4, "=")
    const decoded = atob(normalized)
    return JSON.parse(decoded) as T
  } catch {
    return null
  }
}

export function decodeSupabaseJwtSub(accessToken: string) {
  const payload = decodeBase64UrlJson<{ sub?: string }>(String(accessToken || "").split(".")[1] || "")
  return String(payload?.sub || "").trim()
}

export async function getSupabaseAuthContext(): Promise<SupabaseAuthContext> {
  let session = await supabase.auth.getSession().then((r) => r.data.session).catch(() => null)
  if (!session?.access_token) {
    session = await supabase.auth.refreshSession().then((r) => r.data.session).catch(() => session)
  }

  const accessToken = String(session?.access_token || "").trim()
  const sessionUserId = String(session?.user?.id || "").trim()
  const jwtSub = decodeSupabaseJwtSub(accessToken)
  const authUid = jwtSub || sessionUserId

  if (!accessToken || !authUid) {
    const err: any = new Error("UNAUTHENTICATED")
    err.code = "UNAUTHENTICATED"
    throw err
  }

  if (jwtSub && sessionUserId && jwtSub !== sessionUserId) {
    const err: any = new Error("AUTH_SESSION_MISMATCH")
    err.code = "AUTH_SESSION_MISMATCH"
    err.jwtSub = jwtSub
    err.sessionUserId = sessionUserId
    throw err
  }

  return { accessToken, sessionUserId, jwtSub, authUid }
}

export const PROFILE_AVATAR_BUCKET = String(import.meta.env.VITE_SUPABASE_AVATAR_BUCKET || "avatars").trim() || "avatars"

export type StorageBucketHealth = {
  ok: boolean
  bucket: string
  kind?: "bucket_not_found" | "permission_denied" | "network_error" | "unknown"
  message?: string
}

export function classifyStorageError(err: unknown): StorageBucketHealth["kind"] {
  const msg = String((err as any)?.message || (err as any)?.error || "").toLowerCase()
  const status = Number((err as any)?.statusCode || (err as any)?.status || 0)
  if (msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("load failed") || msg.includes("fetch")) return "network_error"
  if (msg.includes("bucket not found") || msg.includes("bucket") && msg.includes("not found")) return "bucket_not_found"
  if (status === 401 || status === 403 || msg.includes("not authorized") || msg.includes("permission")) return "permission_denied"
  return "unknown"
}

export async function checkStorageBucket(bucket: string = PROFILE_AVATAR_BUCKET): Promise<StorageBucketHealth> {
  try {
    const res = await supabase.storage.from(bucket).list("", { limit: 1 })
    if (res.error) {
      return { ok: false, bucket, kind: classifyStorageError(res.error), message: res.error.message }
    }
    return { ok: true, bucket }
  } catch (e) {
    return { ok: false, bucket, kind: classifyStorageError(e), message: e instanceof Error ? e.message : String(e) }
  }
}
